import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Shield, Trash2 } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/common/ui/card";
import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from "@/common/ui/empty";
import { Skeleton } from "@/common/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/common/ui/tooltip";
import { PermissionGate } from "@/lib/permissions";
import { usePermission } from "@/lib/use-permission";

import type { EnterpriseRole } from "../types";
import { CreateRoleModal } from "./CreateRoleModal";
import { DeleteRoleDialog } from "./DeleteRoleDialog";
import { EditRoleModal } from "./EditRoleModal";
import { rolesKey, useRoles } from "./use-roles";

interface RolesSectionProps {
  enterpriseId: string;
}

export function RolesSection({ enterpriseId }: RolesSectionProps): React.ReactElement {
  const canManageRoles = usePermission("members:update_roles");
  const queryClient = useQueryClient();
  const { roles, isLoading, error } = useRoles(enterpriseId);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EnterpriseRole | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EnterpriseRole | null>(null);

  const invalidateRoles = (): void => {
    void queryClient.invalidateQueries({ queryKey: rolesKey(enterpriseId) });
  };

  if (isLoading) {
    return (
      <div data-testid="roles-loading" className="space-y-3">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Empty>
            <EmptyMedia variant="icon">
              <Shield className="h-6 w-6" />
            </EmptyMedia>
            <EmptyTitle>Failed to load roles</EmptyTitle>
            <EmptyDescription>{error}</EmptyDescription>
            <Button onClick={invalidateRoles}>Retry</Button>
          </Empty>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="roles-section">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Roles</CardTitle>
          <CardDescription>
            Define what members can do. System roles ship with sensible defaults; create custom
            roles for specialized access.
          </CardDescription>
        </div>
        <PermissionGate permission="members:update_roles">
          <Button data-testid="roles-create-cta" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Role
          </Button>
        </PermissionGate>
      </CardHeader>
      <CardContent>
        {roles.length === 0 ? (
          <Empty>
            <EmptyMedia variant="icon">
              <Shield className="h-6 w-6" />
            </EmptyMedia>
            <EmptyTitle>No roles yet</EmptyTitle>
            <EmptyDescription>
              Roles will appear here once they&rsquo;re seeded for this enterprise.
            </EmptyDescription>
          </Empty>
        ) : (
          <ul className="divide-y">
            {roles.map((role) => (
              <li
                key={role.id}
                data-testid={`role-row-${role.id}`}
                className="flex items-start justify-between gap-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{role.name}</p>
                    <Badge variant={role.is_system ? "secondary" : "outline"}>
                      {role.is_system ? "System" : "Custom"}
                    </Badge>
                  </div>
                  {role.description && (
                    <p className="mt-1 text-sm text-muted-foreground">{role.description}</p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {role.permissions.length}{" "}
                    {role.permissions.length === 1 ? "permission" : "permissions"}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`role-edit-${role.id}`}
                            onClick={() => setEditTarget(role)}
                            disabled={!canManageRoles}
                            aria-label={`Edit ${role.name}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!canManageRoles && (
                        <TooltipContent>
                          You need the &ldquo;members:update_roles&rdquo; permission
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  {!role.is_system && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`role-delete-${role.id}`}
                              onClick={() => setDeleteTarget(role)}
                              disabled={!canManageRoles}
                              aria-label={`Delete ${role.name}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {!canManageRoles && (
                          <TooltipContent>
                            You need the &ldquo;members:update_roles&rdquo; permission
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <CreateRoleModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        enterpriseId={enterpriseId}
        onCreated={invalidateRoles}
      />

      {editTarget && (
        <EditRoleModal
          open={editTarget !== null}
          onOpenChange={(open) => {
            if (!open) setEditTarget(null);
          }}
          role={editTarget}
          onSaved={invalidateRoles}
        />
      )}

      {deleteTarget && (
        <DeleteRoleDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          role={deleteTarget}
          onDeleted={invalidateRoles}
        />
      )}
    </Card>
  );
}
